import DesignTokens from '@tableau/design-tokens';
import {
  RadioButtonWithLabelGroupWidget,
  RadioButtonWithLabelOption,
  TabStyles
} from '@tableau/widgets';
import { DataGrid } from '@tableau/widgets-datagrid';
import React from 'react';
import { defaultMemoize } from 'reselect';
import _ from 'underscore';

import { applyColor }from '~src/style/ColorService';

import { Messages } from '../../codegen/Localize';
import { SortableDataGrid } from '../../commonInfra/components/dataGrid/SortableDataGrid3';
import { ISliceable } from '../../httpClient/fetchers/ISliceable';
import { SliceableHelper } from '../../httpClient/utils/SliceableHelper';
import { classes, cssClass } from '../../style';
import { FlowOutputStepInfo } from '../models/FlowOutputStepInfo';
import { FlowOutputStepGrid } from './content/FlowOutputStepGrid';

const cssPrefix = 'FlowOutputsSelection';

const rootClass = cssClass(cssPrefix, 'root', {
  display: 'flex',
  flexDirection: 'column',
  flex: '1 0 auto',
  marginTop: TabStyles.Spacing.MediumSpace,
  padding: `${TabStyles.Spacing.SmallSpace}px ${TabStyles.Spacing.SmallSpace}px 0 ${TabStyles.Spacing.SmallSpace}px`
});

const noOutputPrompt = cssClass(cssPrefix, 'noOutputPrompt', {
  marginTop: TabStyles.Spacing.ExtraLargeSpace,
  marginBottom: TabStyles.Spacing.LargeSpace,
  color: applyColor(DesignTokens.color.font.default.error, TabStyles.Colors.AttentionDark)
});

function getRowData(
  outputStepInfos: ReadonlyArray<FlowOutputStepInfo>
): () => ISliceable<any> {
  const rowArray = outputStepInfos.map(outputStep => {
    return {
      ...outputStep
    };
  });
  return () => SliceableHelper.fromArray(rowArray);
}

function getColumns(
  selectAll: boolean,
  isCheckboxHeaderChecked: boolean,
  toggleSelectAll: () => void,
  onCheckboxClicked: (row: DataGrid.Row) => void,
  onIsIncrementalChange: (row: DataGrid.Row, isIncremental: boolean) => void,
  outputsSelected: { readonly [key: string]: boolean },
  outputsIsIncremental: { readonly [key: string]: boolean },
  isIncrementalEnabled: boolean,
  isRunAllIncremental: boolean,
): DataGrid.Column[] {
  return FlowOutputStepGrid.columns_v1(
    isCheckboxHeaderChecked,
    toggleSelectAll,
    onCheckboxClicked,
    onIsIncrementalChange,
    outputsSelected,
    outputsIsIncremental,
    isIncrementalEnabled,
    isRunAllIncremental,
    selectAll
  );
}

export type FlowOutputsSelectionProps = {
  readonly outputs: ReadonlyArray<FlowOutputStepInfo>;
  readonly selectAll: boolean;
  readonly outputsSelected: { readonly [key: string]: boolean };
  readonly outputsIsIncremental: { readonly [key: string]: boolean };
  readonly isRunAllIncremental: boolean;
  readonly handleChildChange: (id: string) => void;
  readonly handleChildIsIncrementalChange: (id: string, isIncremental: boolean) => void;
  readonly toggleIncludeAll: () => void;
  readonly toggleSelectAll: () => void;
};

export class FlowOutputsSelection extends React.PureComponent<FlowOutputsSelectionProps> {

  private memoizedGetRowData = defaultMemoize(getRowData);
  private memoizedColumns = defaultMemoize(getColumns);


  constructor(props: FlowOutputsSelectionProps) {
    super(props);
  }

  private radioButtonOptions: RadioButtonWithLabelOption<String>[] = [
    {
      value: 'selectAll',
      testId: 'flowOutputsSelectionSelectAll',
      label: Messages.includeAllOutputStepsDetails()
    },
    {
      value: 'selectOutputs',
      testId: 'flowOutputsSelectionSelectOutputs',
      label: Messages.scheduleFlowChooseOutputMessage()
    }
  ];

  private onCheckboxClicked = (row: DataGrid.Row) => {
    this.props.handleChildChange(row.key);
  };

  private onIsIncrementalChange = (row: DataGrid.Row, isIncremental: boolean) => {
    this.props.handleChildIsIncrementalChange(row.key, isIncremental);
  };

  private handleSelect = (value: string, checkedIndex: number) => {
    if (value === 'selectAll' && !this.props.selectAll
     || value === 'selectOutputs' && this.props.selectAll) {
      this.props.toggleIncludeAll();
    }
  };

  private renderNoOutputs(): JSX.Element {
    return this.props.outputs !== null ?
      <div className={noOutputPrompt}>{Messages.flowOutputsEmptyMessage()}</div>
      : <div style={{ height: 177 }}/>; // Height of base grid to minimize resize shifting in the dialog
  }

  private renderRadioButtons(): JSX.Element {
    return (
      <RadioButtonWithLabelGroupWidget
        aria-label={Messages.flowOutputSelectionDescription()}
        buttonList={this.radioButtonOptions}
        checkedIndex={this.props.selectAll ? 0 : 1}
        handleChange={this.handleSelect}
      />
    );
  }

  private renderGrid(): JSX.Element {
    const outputsSelectedCount = _.reduce(this.props.outputsSelected, (count: number, value: boolean) => {
      return value && count + 1;
    }, 0);
    const isIncrementalEnabled = !!this.props.outputs.find(output => output.isIncrementalEnabled);

    const columns = this.memoizedColumns(
      this.props.selectAll,
      outputsSelectedCount === this.props.outputs.length,
      this.props.toggleSelectAll,
      this.onCheckboxClicked,
      this.onIsIncrementalChange,
      this.props.outputsSelected,
      this.props.outputsIsIncremental,
      isIncrementalEnabled,
      this.props.isRunAllIncremental,
    );
    return (
      <SortableDataGrid
        columns={columns}
        getSliceable={this.memoizedGetRowData(this.props.outputs)}
        dataGridProps={{
          showBorder: true,
          showVerticalGridLines: true,
          style: { marginTop: TabStyles.Spacing.MediumSpace }
        }}
      />
    );
  }

  public render(): JSX.Element {
    return (
      this.props.outputs && this.props.outputs.length > 0 ?
        <div className={classes(rootClass)}>
          {this.renderRadioButtons()}
          {this.renderGrid()}
        </div>
        : this.renderNoOutputs()
    );
  }
}
